import React, { useState } from "react";
import deleteIcon from "Assets/Icons/delete.svg";
import useVipApiFetcher, {
  handleError,
  handleResponse,
  handleOkResponse,
} from "Utils/Hooks/useVipApiFetcher";
import { endingIn } from "Utils/Utils";
import { Routes } from "Utils/Constants";
import { useHistory } from "react-router-dom";
import useMode, { MODE } from "Utils/Hooks/useMode";
import useFundType, { FUNDTYPE } from "Utils/Hooks/useFundType";
import { faro } from "@grafana/faro-react";
import { Button } from "@pav/pav-products-design-system";
import DeleteAccountModal from "./DeleteAccountModal";
import Loading from "Components/Loading";
import useSession from "Utils/Hooks/useSession";

const DeleteAccountButton = ({ account, bankAccountData }) => {
  const { session } = useSession();
  const fundType = useFundType();
  const vipApi = useVipApiFetcher();
  const history = useHistory();
  const [submitted, setSubmitted] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const handleClose = () => setShowConfirmation(false);
  const handleShow = () => {
    setShowConfirmation(true);
    faro.api.pushEvent("User clicked delete Funding Source icon");
  };

  const mode = useMode();
  const isDepositMode = mode === MODE.deposit;

  const onDeleteBank = async () => {
    setSubmitted(true);
    const body = {
      bankName: account.bankName,
      routingNumber: account.routingNumber,
      accountNumber: account.accountNumber,
      maskedAccountNumber: account.maskedAccountNumber,
      netspendCardReferenceNumber: account.netspendCardReferenceNumber,
    };

    vipApi
      .fetch("/api/bank", "DELETE", body)
      .then((response) => handleResponse(history, response))
      .then((response) =>
        handleOkResponse({
          history,
          response,
          title:
            fundType === FUNDTYPE.netspend
              ? "Netspend/Playcard Account Deleted"
              : "Bank Account Deleted",
          message: `Your ${response.body.bankName} account${endingIn(account.accountNumberDisplay)} has been successfully deleted.`,
          customButtons: true
            ? [
                {
                  label:
                    mode === "managebanks"
                      ? "MANAGE FUNDING SOURCES"
                      : "ADD NEW FUNDING SOURCE",
                  route:
                    mode === "managebanks"
                      ? Routes.MY_ACCOUNTS
                      : Routes.ADD_ACCOUNT,
                  style: "secondary",
                },
                {
                  label:
                    mode === "managebanks"
                      ? "DONE"
                      : `COMPLETE ${isDepositMode ? "DEPOSIT" : "WITHDRAW"}`,
                  route:
                    mode === "managebanks"
                      ? undefined
                      : isDepositMode
                        ? Routes.DEPOSIT
                        : Routes.WITHDRAW,
                  style: mode === "managebanks" ? "primary" : undefined,
                  returnToOperator: mode === "managebanks",
                  returnUrl: session?.returnUrl,
                  transactionId:
                    mode === "managebanks" ? session?.transactionId : undefined,
                  transactionType:
                    mode === "managebanks" ? "bankManagement" : undefined,
                },
              ]
            : undefined,
          errorCustomButtons: true
            ? [
                {
                  label: "TRY AGAIN",
                  route: Routes.MY_ACCOUNTS,
                },
                {
                  label: `Complete ${isDepositMode ? "Deposit" : "Withdraw"}`,
                  route: isDepositMode ? Routes.DEPOSIT : Routes.WITHDRAW,
                },
              ]
            : undefined,
        }),
      )
      .catch((error) => {
        faro.api.pushError(new Error("Delete Account Network Error"), {
          context: error,
        });
        handleError(error, history);
      });
    faro.api.pushEvent("User confirms to delete Funding Source");
  };

  const minLimitReached = bankAccountData?.length === 1;
  const title = minLimitReached ? "Add a bank account first" : "Delete Account";
  const bodyText = minLimitReached ? (
    "You need to have at least one bank account linked to your profile. Please add another account before removing this one."
  ) : (
    <>
      Are you sure you want to delete your {account.bankName?.toUpperCase()}{" "}
      account{endingIn(account.accountNumberDisplay)}?
    </>
  );
  const buttons = minLimitReached ? (
    <Button
      fullWidth
      className="submit-button click-delete-confirm-ok"
      onClick={handleClose}
    >
      Ok
    </Button>
  ) : (
    <>
      {submitted ? (
        <Loading />
      ) : (
        <Button
          className="submit-button click-delete-confirm-yes"
          onClick={onDeleteBank}
          buttonType="primary"
        >
          YES
        </Button>
      )}

      <Button
        className="submit-button click-delete-confirm-cancel"
        onClick={handleClose}
        buttonType="secondary"
      >
        NO
      </Button>
    </>
  );

  return (
    <>
      <button onClick={handleShow} className="delete click-delete">
        <img alt="Delete Account" src={deleteIcon} className="click-delete" />
      </button>
      <DeleteAccountModal
        open={showConfirmation}
        title={title}
        body={bodyText}
        buttons={buttons}
      />
    </>
  );
};

export default DeleteAccountButton;
